import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  IconButton,
  Badge,
  Switch,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  useToast,
  useColorModeValue,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import { FiUsers, FiUserPlus, FiTrash2, FiEye } from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useCrmUsers } from '../../../hooks/useCrmUsers';

/**
 * MyTeamSettings — book sharing (multi-owner Phase 2).
 *
 * The team owner (or an admin acting for them via the settings user picker)
 * grants other CRM users standing access to their whole book: every contact
 * and/or deal they own now or create later. Visibility is resolved live by
 * the record-visibility predicate — nothing is copied.
 *
 * All writes go through the manageCrmTeam callable (tables are
 * service-role-write-only) which also writes crm_audit_logs.
 */
export default function MyTeamSettings({ userId, isAdmin = false }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const { users: crmUsers, loading: usersLoading, getUserName } = useCrmUsers();

  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingUid, setPendingUid] = useState(null); // member row with an in-flight update
  const [newMember, setNewMember] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const cancelRemoveRef = React.useRef(null);

  const cardBg = useColorModeValue('gray.50', 'gray.750');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const isOwnBook = userId === currentUser?.uid;
  const ownerLabel = isOwnBook ? 'your' : `${getUserName(userId)}'s`;

  // ------------------------------------------------------------------ load
  const loadTeam = useCallback(async () => {
    if (!userId || !isSupabaseConfigured()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: teamRow, error: teamError } = await supabase
        .from('crm_teams')
        .select('id, name, owner_uid')
        .eq('owner_uid', userId)
        .maybeSingle();
      if (teamError) throw teamError;
      setTeam(teamRow || null);

      if (teamRow) {
        const { data: memberRows, error: membersError } = await supabase
          .from('crm_team_members')
          .select('id, member_uid, access_level, share_contacts, share_deals, created_at')
          .eq('team_id', teamRow.id)
          .order('created_at', { ascending: true });
        if (membersError) throw membersError;
        setMembers(memberRows || []);
      } else {
        setMembers([]);
      }

      // Books shared WITH this user (read-only info)
      const { data: grants, error: grantsError } = await supabase
        .from('crm_team_members')
        .select('id, share_contacts, share_deals, created_at, crm_teams!inner(owner_uid, name)')
        .eq('member_uid', userId);
      if (grantsError) throw grantsError;
      setSharedWithMe(grants || []);
    } catch (err) {
      console.error('[MyTeamSettings] load error:', err);
      toast({ title: 'Error loading team', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  // ------------------------------------------------------------- mutations
  const callManageTeam = useCallback(async (payload) => {
    const fn = httpsCallable(functionsUsEast1, 'manageCrmTeam');
    await fn({ ownerUid: userId, ...payload });
  }, [userId]);

  const handleAddMember = async () => {
    if (!newMember?.value) return;
    setIsAdding(true);
    try {
      await callManageTeam({ action: 'add_member', memberUid: newMember.value });
      toast({
        title: 'Team member added',
        description: `${newMember.label} can now see ${ownerLabel} contacts and deals.`,
        status: 'success',
        duration: 4000,
      });
      setNewMember(null);
      await loadTeam();
    } catch (err) {
      console.error('[MyTeamSettings] add member error:', err);
      toast({ title: 'Error adding member', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggle = async (member, field) => {
    const next = {
      shareContacts: field === 'contacts' ? !member.share_contacts : member.share_contacts,
      shareDeals: field === 'deals' ? !member.share_deals : member.share_deals,
    };
    if (!next.shareContacts && !next.shareDeals) {
      toast({
        title: 'At least one must stay on',
        description: 'Remove the member instead of turning off both contacts and deals.',
        status: 'warning',
        duration: 4000,
      });
      return;
    }
    setPendingUid(member.member_uid);
    // Optimistic update
    setMembers(prev => prev.map(m => (m.member_uid === member.member_uid
      ? { ...m, share_contacts: next.shareContacts, share_deals: next.shareDeals }
      : m)));
    try {
      await callManageTeam({ action: 'update_member', memberUid: member.member_uid, ...next });
    } catch (err) {
      console.error('[MyTeamSettings] toggle error:', err);
      // Rollback
      setMembers(prev => prev.map(m => (m.member_uid === member.member_uid ? member : m)));
      toast({ title: 'Error updating member', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setPendingUid(null);
    }
  };

  const handleRemove = async () => {
    const target = removeTarget;
    setRemoveTarget(null);
    if (!target) return;
    setPendingUid(target.member_uid);
    try {
      await callManageTeam({ action: 'remove_member', memberUid: target.member_uid });
      toast({
        title: 'Team member removed',
        description: `${getUserName(target.member_uid)} no longer sees ${ownerLabel} records.`,
        status: 'success',
        duration: 4000,
      });
      await loadTeam();
    } catch (err) {
      console.error('[MyTeamSettings] remove error:', err);
      toast({ title: 'Error removing member', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setPendingUid(null);
    }
  };

  // ------------------------------------------------------------------- UI
  const memberUids = useMemo(() => new Set(members.map(m => m.member_uid)), [members]);
  const candidateOptions = useMemo(() =>
    crmUsers
      .filter(u => u.uid !== userId && !memberUids.has(u.uid))
      .map(u => ({ value: u.uid, label: u.name || u.email, email: u.email })),
    [crmUsers, userId, memberUids]
  );

  if (loading || usersLoading) {
    return (
      <Center py={10}>
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <HStack spacing={2} mb={1}>
          <FiUsers />
          <Heading size="md">{isOwnBook ? 'My Team' : `${getUserName(userId)}'s Team`}</Heading>
        </HStack>
        <Text fontSize="sm" color="gray.600">
          Team members see {ownerLabel} contacts and deals in their own Origin — including
          records created later. They never need to impersonate or switch views.
        </Text>
      </Box>

      <Alert status="info" borderRadius="md" fontSize="sm">
        <AlertIcon />
        Sharing is live: adding someone grants access immediately, removing them revokes it
        immediately. Every change is written to the audit log.
      </Alert>

      {/* Add member */}
      <Box p={4} bg={cardBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
        <Text fontWeight="semibold" fontSize="sm" mb={3}>Add a team member</Text>
        <HStack align="flex-end" spacing={3}>
          <Box flex={1} maxW="420px">
            <Select
              value={newMember}
              onChange={setNewMember}
              options={candidateOptions}
              placeholder="Select a CRM user…"
              isClearable
              size="sm"
            />
          </Box>
          <Button
            size="sm"
            colorScheme="blue"
            leftIcon={<FiUserPlus />}
            onClick={handleAddMember}
            isLoading={isAdding}
            isDisabled={!newMember}
          >
            Share Book
          </Button>
        </HStack>
      </Box>

      {/* Members table */}
      {members.length === 0 ? (
        <Box p={6} textAlign="center" borderWidth="1px" borderStyle="dashed" borderColor={borderColor} borderRadius="lg">
          <Text fontSize="sm" color="gray.500">
            No team members yet. {isOwnBook ? 'Your' : 'This'} book is visible only to {isOwnBook ? 'you' : 'its owner'} (and CRM admins).
          </Text>
        </Box>
      ) : (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflow="hidden">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Member</Th>
                <Th textAlign="center">Contacts</Th>
                <Th textAlign="center">Deals</Th>
                <Th textAlign="center">Since</Th>
                <Th w="60px" />
              </Tr>
            </Thead>
            <Tbody>
              {members.map(member => (
                <Tr key={member.id}>
                  <Td>
                    <Text fontWeight="medium" fontSize="sm">{getUserName(member.member_uid)}</Text>
                  </Td>
                  <Td textAlign="center">
                    <Switch
                      size="sm"
                      colorScheme="blue"
                      isChecked={member.share_contacts}
                      isDisabled={pendingUid === member.member_uid}
                      onChange={() => handleToggle(member, 'contacts')}
                    />
                  </Td>
                  <Td textAlign="center">
                    <Switch
                      size="sm"
                      colorScheme="blue"
                      isChecked={member.share_deals}
                      isDisabled={pendingUid === member.member_uid}
                      onChange={() => handleToggle(member, 'deals')}
                    />
                  </Td>
                  <Td textAlign="center">
                    <Text fontSize="xs" color="gray.500">
                      {member.created_at ? new Date(member.created_at).toLocaleDateString() : '—'}
                    </Text>
                  </Td>
                  <Td>
                    <Tooltip label="Remove from team (revokes access immediately)">
                      <IconButton
                        icon={<FiTrash2 />}
                        aria-label="Remove member"
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        isLoading={pendingUid === member.member_uid}
                        onClick={() => setRemoveTarget(member)}
                      />
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Books shared with this user */}
      <Box>
        <HStack spacing={2} mb={2}>
          <FiEye />
          <Heading size="sm">{isOwnBook ? 'Shared with me' : `Shared with ${getUserName(userId)}`}</Heading>
        </HStack>
        {sharedWithMe.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            No one has shared their book {isOwnBook ? 'with you' : 'with this user'} yet.
          </Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            {sharedWithMe.map(grant => (
              <HStack key={grant.id} p={3} bg={cardBg} borderRadius="md" borderWidth="1px" borderColor={borderColor} justify="space-between">
                <Text fontSize="sm" fontWeight="medium">
                  {getUserName(grant.crm_teams?.owner_uid)}
                </Text>
                <HStack spacing={2}>
                  {grant.share_contacts && <Badge colorScheme="blue" fontSize="xs">Contacts</Badge>}
                  {grant.share_deals && <Badge colorScheme="green" fontSize="xs">Deals</Badge>}
                </HStack>
              </HStack>
            ))}
          </VStack>
        )}
      </Box>

      {/* Remove confirmation */}
      <AlertDialog
        isOpen={!!removeTarget}
        leastDestructiveRef={cancelRemoveRef}
        onClose={() => setRemoveTarget(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove team member
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text fontSize="sm">
                {removeTarget ? getUserName(removeTarget.member_uid) : ''} will immediately lose
                access to {ownerLabel} contacts and deals. Campaigns they enrolled shared
                records into keep running — only visibility is revoked.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRemoveRef} onClick={() => setRemoveTarget(null)} size="sm">
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleRemove} ml={3} size="sm">
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
}
